/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { DomController } from '../services/DomController';
import { GeminiService } from '../services/GeminiService';
import { DriveService } from '../services/DriveService';
import { AudioService } from '../services/AudioService';
import { NoteService } from '../services/NoteService';
import { UIService } from '../services/UIService';
import { SettingsService } from '../services/SettingsService';
import { KeyboardService } from '../services/KeyboardService';
import { EditorService } from '../services/EditorService';
import { DBService } from '../services/DBService';
import { AuthService } from '../services/AuthService';
import { PaneService } from '../services/PaneService';
import { DialogService } from '../services/DialogService';
import { EditorActionService } from '../services/EditorActionService';
import { ServiceRegistry } from '../utils/ServiceRegistry';

/**
 * The main application class, acting as a controller.
 * It initializes and coordinates all the specialized services.
 */
export class AppController {
  private dom: DomController;
  private gemini: GeminiService;
  private drive: DriveService;
  private dbService: DBService;

  private settingsService: SettingsService;
  private uiService: UIService;
  private noteService: NoteService;
  private editorService: EditorService;
  private authService: AuthService;
  private audioService: AudioService;
  private editorActionService: EditorActionService;
  private dialogService: DialogService;
  private paneService: PaneService;
  private keyboardService: KeyboardService;

  constructor() {
    // Foundational services - instantiate first
    this.dom = new DomController();
    this.gemini = new GeminiService();
    this.dbService = new DBService();
    
    // Register foundational services immediately
    ServiceRegistry.register('dom', this.dom);
    ServiceRegistry.register('gemini', this.gemini);
    ServiceRegistry.register('dbService', this.dbService);
    
    // Now instantiate services that depend on foundational ones
    this.drive = new DriveService();
    ServiceRegistry.register('drive', this.drive);
    
    // Core state and UI services
    this.settingsService = new SettingsService();
    this.noteService = new NoteService();
    this.noteService.injectDriveService(this.drive);
    this.uiService = new UIService();
    this.editorService = new EditorService();
    
    // Register core services
    ServiceRegistry.register('settingsService', this.settingsService);
    ServiceRegistry.register('noteService', this.noteService);
    ServiceRegistry.register('uiService', this.uiService);
    ServiceRegistry.register('editorService', this.editorService);

    // Feature services that depend on core services
    this.authService = new AuthService();
    this.audioService = new AudioService();
    this.dialogService = new DialogService();
    this.editorActionService = new EditorActionService();
    this.paneService = new PaneService();
    
    // Register feature services
    ServiceRegistry.register('authService', this.authService);
    ServiceRegistry.register('audioService', this.audioService);
    ServiceRegistry.register('dialogService', this.dialogService);
    ServiceRegistry.register('editorActionService', this.editorActionService);
    ServiceRegistry.register('paneService', this.paneService);
    
    // Inject services post-construction where circular references are unavoidable
    this.injectDependencies();
    
    // Keyboard service last, as it depends on many other services
    this.keyboardService = new KeyboardService();
    ServiceRegistry.register('keyboardService', this.keyboardService);
    
    // Mark registry as initialized
    ServiceRegistry.markInitialized();
    
    this.bindEventListeners();
    this._start();
  }
  
  private injectDependencies(): void {
    // Inject services post-construction to resolve circular dependencies
    this.dialogService.injectAudioService(this.audioService);
    this.dialogService.injectPaneService(this.paneService);
    this.dialogService.injectAuthService(this.authService);

    this.dom.injectNoteService(this.noteService);
  }

  private bindEventListeners(): void {
    this.dom.recordButton.addEventListener('click', () => this.audioService.toggleRecording());
    this.dom.cancelRecordingButton.addEventListener('click', () => this.audioService.cancelRecording());
    this.dom.sourceViewToggle.addEventListener('change', () => this.handleSourceViewToggle());
    this.dom.newButton.addEventListener('click', () => this.dialogService.handleNewNoteClick());
    this.dom.saveNoteButton.addEventListener('click', () => this.handleSaveNote());
    this.dom.clearLocalDataButton.addEventListener('click', () => this.handleClearLocalData());

    const dropZone = this.dom.noteContentWrapper;
    dropZone.addEventListener('dragover', (e) => this.handleDragOver(e as DragEvent));
    dropZone.addEventListener('dragleave', (e) => this.handleDragLeave(e as DragEvent));
    dropZone.addEventListener('drop', (e) => this.handleDrop(e as DragEvent));

    
    this.dom.actionVoiceButton.addEventListener('click', () => this.audioService.toggleCommandRecording());
    this.dom.actionInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.handleActionExecution();
        }
    });
  }
  
  private async _start(): Promise<void> {
    const isProduction = process.env.CI === 'true' || process.env.CF_PAGES === '1';

    if (isProduction) {
        document.body.classList.add('auth-required');
        this.authService.onLoginSuccess = () => {
            document.body.classList.remove('auth-required');
            this.onAuthenticationSuccess();
        };
        await this.authService.initGoogleDrive(true);
    } else {
        // Always require authentication for security
        document.body.classList.add('auth-required');
        this.authService.onLoginSuccess = () => {
            document.body.classList.remove('auth-required');
            this.onAuthenticationSuccess();
        };
        await this.authService.initGoogleDrive(false);
    }
  }

  private async onAuthenticationSuccess(): Promise<void> {
    // Set the current user for encryption
    const currentUser = this.drive.currentUser;
    if (currentUser) {
        this.dbService.setCurrentUser(currentUser);
    }

    // Now initialize the app
    await this.initializeApp();
  }

  private async initializeApp(): Promise<void> {
    await this.settingsService.initSettings();
    const theme = await this.settingsService.getTheme();
    this.dom.initTheme(theme);
    this.uiService.initInitialUIState();
    // Initialize source view state
    this.dom.setSourceViewState(this.settingsService.sourceView);
    await this.noteService.loadMostRecentNote();
    this.noteService.startAutosave();

    const initiallyActiveButton = document.querySelector(".tab-navigation .tab-button.active") as HTMLButtonElement;
    if (initiallyActiveButton) {
        requestAnimationFrame(() => {
            this.dom.setActiveTab(initiallyActiveButton, true);
            this.editorService.updateActiveEditorForHistory();
        });
    }

    this.dom.updateStatus('Ready to record');
  }

  private handleDragOver(e: DragEvent): void {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'copy';
      }
      this.dom.noteContentWrapper.classList.add('drag-over');
  }

  private handleDragLeave(e: DragEvent): void {
      e.preventDefault();
      e.stopPropagation();
      this.dom.noteContentWrapper.classList.remove('drag-over');
  }
  
  private async handleDrop(e: DragEvent): Promise<void> {
      e.preventDefault();
      e.stopPropagation();
      this.dom.noteContentWrapper.classList.remove('drag-over');

      if (e.dataTransfer?.files.length) {
          const file = e.dataTransfer.files[0];
          const isMarkdown = file.type === 'text/markdown' || file.name.endsWith('.md');
          const isText = file.type === 'text/plain' || file.name.endsWith('.txt');

          if (isMarkdown || isText) {
              const fileContent = await file.text();

              const note = this.noteService.currentNote;
              const hasContent = note && (note.rawTranscription.trim() || note.polishedNote.trim() || note.translatedNote.trim());
              
              if (hasContent || this.noteService.isDirty) {
                  const action = await this.dialogService.confirmNewNote();
                  if (action === 'cancel') {
                      return;
                  }
                  if (action === 'save') {
                      this.editorService.syncActiveContentToModel();
                      this.noteService.syncUItoModel(false);
                      await this.noteService.saveCurrentNote();
                  }
              }
              
              await this.noteService.createNewNote();
              if (this.noteService.currentNote) {
                  this.noteService.currentNote.polishedNote = fileContent;
                  this.noteService.currentNote.title = file.name.replace(/\.(md|txt)$/i, '');
                  await this.noteService.renderNoteUI();
                  this.editorService.resetHistory('polishedNote');
                  this.editorService.resetHistory('translatedNote');
                  this.editorService.saveState(true);
                  this.noteService.setDirty(true);
                  this.paneService.updateHistoryButtonState();
                  this.dom.updateStatus(`Loaded file: ${file.name}`);
              }

          } else {
              this.dom.updateStatus('Unsupported file type. Please drop a .md or .txt file.');
          }
      }
  }

  private handleSourceViewToggle(): void {
    const activeTab = document.querySelector('.tab-button.active') as HTMLButtonElement;
    const tabName = activeTab?.getAttribute('data-tab');
    
    if (tabName) {
        this.dom.updateRawContent(tabName);
    }
    
    this.dom.setSourceViewState(this.dom.sourceViewToggle.checked);
  }

  private async handleSaveNote(): Promise<void> {
    this.editorService.syncActiveContentToModel();
    this.noteService.syncUItoModel(false);
    
    // Check if note has existing Drive location
    if (this.noteService.currentNote?.driveFileId) {
      // Save to Drive
      await this.saveNoteToDrive();
    } else {
      // Save locally
      await this.noteService.saveCurrentNote(true);
      this.dom.updateStatusWithTimeout('Note saved locally.');
    }
  }

  private async saveNoteToDrive(): Promise<void> {
    if (!this.noteService.currentNote) return;

    try {
      this.dom.updateStatus('Saving to Google Drive...');
      
      // Get content and title
      const noteTitle = this.noteService.currentNote.title || 'Untitled Note';
      const frontMatterParts: string[] = [];
      
      if (this.noteService.currentNote.category) {
        frontMatterParts.push(`category: ${this.noteService.currentNote.category}`);
      }
      if (this.noteService.currentNote.tags && this.noteService.currentNote.tags.length > 0) {
        frontMatterParts.push(`tags: [${this.noteService.currentNote.tags.join(', ')}]`);
      }
      frontMatterParts.push(`created: ${new Date(this.noteService.currentNote.timestamp).toISOString()}`);
      
      const frontMatter = frontMatterParts.length > 0 ? `---\n${frontMatterParts.join('\n')}\n---\n\n` : '';
      
      const contentParts: string[] = [];
      if (this.noteService.currentNote.polishedNote) {
        contentParts.push(`**Polished Note**\n\n${this.noteService.currentNote.polishedNote}`);
      }
      if (this.noteService.currentNote.rawTranscription) {
        contentParts.push(`**Raw Transcription**\n\n${this.noteService.currentNote.rawTranscription}`);
      }
      if (this.noteService.currentNote.translatedNote) {
        contentParts.push(`**Translated Content**\n\n${this.noteService.currentNote.translatedNote}`);
      }
      
      const noteContent = frontMatter + contentParts.join('\n\n---\n\n');
      
      // Update existing Drive file (saveNote handles both create and update)
      const { webViewLink } = await this.noteService.driveService.saveNote(
        noteContent,
        noteTitle,
        this.noteService.currentNote
      );
      
      // Update timestamp and save locally
      this.noteService.currentNote.modifiedTimestamp = Date.now();
      await this.noteService.saveCurrentNote(false); // Save but keep dirty state
      
      this.dom.updateStatusWithTimeout(`Note updated in Drive. <a href="${webViewLink}" target="_blank" rel="noopener noreferrer">Open in Drive</a>`);
      
    } catch (error: any) {
      console.error('Error saving to Drive:', error);
      const errorMessage = error.result?.error?.message || 'An unknown error occurred.';
      this.dom.updateStatusWithTimeout(`Error saving to Drive: ${errorMessage}`);
      
      if (error.status === 401 || error.status === 403) {
        (window as any).gapi.client.setToken(null);
        this.dom.updateStatus('Please try saving again.');
      }
    }
  }

  private async handleClearLocalData(): Promise<void> {
    // Show confirmation dialog
    const confirmed = confirm(
      '⚠️ WARNING: This will permanently delete ALL local data including:\n\n' +
      '• All notes and their content\n' +
      '• Note history and versions\n' +
      '• Application settings\n' +
      '• Authentication tokens\n\n' +
      'This action cannot be undone. Are you sure you want to continue?'
    );

    if (!confirmed) return;

    try {
      await this.noteService.clearAllLocalData();
      
      // Close settings popover
      this.dom.settingsPopover.classList.add('hidden');
      
      // Show success message
      this.dom.updateStatus('All local data cleared successfully');
      
      // The app will need to be reloaded to fully reset state
      setTimeout(() => {
        const reloadConfirmed = confirm(
          'The app needs to reload to complete the data clearing process.\n\n' +
          'Would you like to reload now?'
        );
        if (reloadConfirmed) {
          window.location.reload();
        }
      }, 1000);
      
    } catch (error) {
      console.error('Failed to clear local data:', error);
      this.dom.updateStatus('Failed to clear local data');
    }
  }

  private async handleActionExecution(): Promise<void> {
    const prompt = this.dom.actionInput.value.trim();
    if (!prompt) return;

    this.dom.actionOutput.innerHTML = `<div class="action-output-thinking">Thinking...</div>`;
    this.dom.actionOutput.classList.add('visible');
    this.dom.actionTestButton.disabled = true;

    try {
        const result = await this.gemini.executeAction(prompt, this.settingsService.getDefaultModel());
        this.dom.actionOutput.innerHTML = result;
    } catch (error) {
        console.error('Action execution failed:', error);
        this.dom.actionOutput.innerHTML = `<div class="action-output-error">An error occurred. See console for details.</div>`;
    } finally {
        this.dom.actionTestButton.disabled = false;
        this.dom.actionInput.value = '';
    }
  }
}